import type { FullPostType, SharingModalFactoryPropsWithResource } from '../../factories/types';
import type { FacebookPost, RedditPost } from '../../types';

import type { ActionsReturnType, GroupSharingHookProps, PostSharingHookProps, StorySharingHookProps } from './types';
import { useFacebookGroupPostSharing } from './useFacebookGroupPostSharing';
import { useFacebookPostSharing } from './useFacebookPostSharing';
import { useFacebookStorySharing } from './useFacebookStorySharing';
import { useInstagramPostSharing } from './useInstagramPostSharing';
import { useInstagramStorySharing } from './useInstagramStorySharing';
import { useRedditGroupPostSharing } from './useRedditGroupPostSharing';

type SharingHook<P> = (props: P) => ActionsReturnType;
type Executor = (props: SharingModalFactoryPropsWithResource) => ActionsReturnType;

const createEntry = <P>(
	hook: SharingHook<P>,
	getProps: (props: SharingModalFactoryPropsWithResource) => P,
): Executor => {
	return (props) => {
		const prepared = getProps(props);
		return hook(prepared);
	};
};

// Add more executors as more post types are supported
export const EXECUTORS: Readonly<Record<FullPostType, Executor>> = {
	facebook_post: createEntry(useFacebookPostSharing, (props): PostSharingHookProps => {
		const { trackingProps } = props;
		if (props.sharingResource && 'message' in props.sharingResource) {
			return {
				message: props.sharingResource.message,
				trackingProps,
				previewImageUrl: '', // this post type doesn't use previewImageUrl for actions but is required by the type
			};
		}

		return {
			message: '',
			previewImageUrl: '',
			trackingProps,
		};
	}),
	facebook_group_post: createEntry(useFacebookGroupPostSharing, (props): GroupSharingHookProps => {
		if (props.sharingResource && 'message' in props.sharingResource) {
			const { trackingProps } = props;

			return {
				recommendedGroup: (props as FacebookPost).recommendedGroup,
				message: props.sharingResource.message,
				trackingProps,
			};
		}

		const { trackingProps } = props;
		return {
			recommendedGroup: (props as FacebookPost).recommendedGroup,
			message: '',
			trackingProps,
		};
	}),
	facebook_story: createEntry(useFacebookStorySharing, (props): StorySharingHookProps => {
		const { trackingProps } = props;
		if (props.sharingResource) {
			const { previewImageUrl } = props.sharingResource;

			if ('shareUrl' in props.sharingResource && previewImageUrl) {
				return { shareUrl: props.sharingResource.shareUrl || '', previewImageUrl, trackingProps };
			}
		}
		return { shareUrl: '', previewImageUrl: '', trackingProps };
	}),
	instagram_post: createEntry(useInstagramPostSharing, (props): PostSharingHookProps => {
		const { trackingProps } = props;
		if (props.sharingResource && 'message' in props.sharingResource && props.sharingResource.previewImageUrl) {
			return {
				message: props.sharingResource.message,
				previewImageUrl: props.sharingResource.previewImageUrl,
				trackingProps,
			};
		}
		return {
			message: '',
			previewImageUrl: '',
			trackingProps,
		};
	}),
	instagram_story: createEntry(useInstagramStorySharing, (props): StorySharingHookProps => {
		const { trackingProps } = props;
		if (props.sharingResource) {
			const { previewImageUrl } = props.sharingResource;

			if ('shareUrl' in props.sharingResource && previewImageUrl) {
				return { shareUrl: props.sharingResource.shareUrl || '', previewImageUrl, trackingProps };
			}
		}
		return { shareUrl: '', previewImageUrl: '', trackingProps };
	}),
	reddit_post: createEntry(useRedditGroupPostSharing, (props): GroupSharingHookProps => {
		if (props.sharingResource && 'message' in props.sharingResource) {
			const { trackingProps } = props;

			return {
				recommendedGroup: (props as RedditPost).recommendedGroup,
				message: props.sharingResource.message,
				trackingProps,
			};
		}
		const { trackingProps } = props;
		return {
			recommendedGroup: (props as RedditPost).recommendedGroup,
			message: '',
			trackingProps,
		};
	}),
	reddit_group_post: createEntry(useRedditGroupPostSharing, (props): GroupSharingHookProps => {
		if (props.sharingResource && 'message' in props.sharingResource && 'shareUrl' in props.sharingResource) {
			const { trackingProps, petition, sharingResource } = props;

			return {
				recommendedGroup: (props as RedditPost).recommendedGroup,
				message: props.sharingResource.message,
				trackingProps,
				petitionTitle: petition.ask,
				shareUrl: sharingResource.shareUrl,
			};
		}
		const { trackingProps, petition } = props;
		return {
			recommendedGroup: (props as RedditPost).recommendedGroup,
			message: '',
			trackingProps,
			petitionTitle: petition.ask,
			shareUrl: '',
		};
	}),
};
